import React, { useState, useEffect } from 'react';
import { Shield, Check, X, ChevronDown, ChevronRight, Plus, Trash2, Clock, User, Save, RotateCcw, Edit2, Lock } from 'lucide-react';
import { api } from '../api/client';

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('roles');
  const [expandedCategories, setExpandedCategories] = useState(new Set(['Portfolio/Program/Project', 'User Management']));
  
  // Configurable matrix state
  const [matrixData, setMatrixData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState({});
  const [savingRole, setSavingRole] = useState(null);
  
  // Overrides state
  const [overrides, setOverrides] = useState([]);
  const [users, setUsers] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [showCreateOverride, setShowCreateOverride] = useState(false);
  const [newOverride, setNewOverride] = useState({
    user_id: '',
    permission_key: '',
    scope_type: 'global',
    scope_id: '',
    duration_days: 30,
    reason: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (activeTab === 'overrides') {
      loadOverrides();
    }
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesRes, matrixRes, usersRes, permsRes] = await Promise.all([
        api.getAdminRoles(),
        api.getConfigurableMatrix(),
        api.getAdminUsers(),
        api.listAllPermissions()
      ]);
      setRoles(rolesRes.data.roles || []);
      setMatrixData(matrixRes.data);
      setUsers(usersRes.data.users || []);
      setAllPermissions(permsRes.data.permissions || []);
      
      // Initialize edited permissions from current matrix
      if (matrixRes.data && matrixRes.data.matrix) {
        const initial = {};
        matrixRes.data.roles.forEach(role => {
          initial[role.key] = [];
          Object.entries(matrixRes.data.matrix).forEach(([cat, perms]) => {
            Object.entries(perms).forEach(([perm, rolePerms]) => {
              if (rolePerms[role.key]) {
                initial[role.key].push(perm);
              }
            });
          });
        });
        setEditedPermissions(initial);
      }
    } catch (error) {
      console.error('Failed to load roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const res = await api.getPermissionOverrides({ include_expired: false });
      setOverrides(res.data.overrides || []);
    } catch (error) {
      console.error('Failed to load overrides:', error);
    }
  };

  const handleTogglePermission = (roleKey, permission) => {
    if (roleKey === 'sysadmin') return; // Can't edit sysadmin
    
    setEditedPermissions(prev => {
      const current = prev[roleKey] || [];
      if (current.includes(permission)) {
        return { ...prev, [roleKey]: current.filter(p => p !== permission) };
      } else {
        return { ...prev, [roleKey]: [...current, permission] };
      }
    });
  };

  const handleSaveRolePermissions = async (roleKey) => {
    try {
      setSavingRole(roleKey);
      await api.updateRolePermissions(roleKey, editedPermissions[roleKey] || []);
      await loadData(); // Reload to get updated matrix
      alert(`Permissions saved for ${roleKey}`);
    } catch (error) {
      alert('Failed to save permissions: ' + error.message);
    } finally {
      setSavingRole(null);
    }
  };

  const handleResetRolePermissions = async (roleKey) => {
    if (!window.confirm(`Reset ${roleKey} permissions to default? This cannot be undone.`)) return;
    try {
      setSavingRole(roleKey);
      await api.resetRolePermissions(roleKey);
      await loadData();
      alert(`Permissions reset for ${roleKey}`);
    } catch (error) {
      alert('Failed to reset permissions: ' + error.message);
    } finally {
      setSavingRole(null);
    }
  };

  const handleCreateOverride = async () => {
    if (!newOverride.user_id || !newOverride.permission_key || !newOverride.reason) {
      alert('Please fill in user, permission, and reason');
      return;
    }
    try {
      await api.createPermissionOverride(newOverride);
      setShowCreateOverride(false);
      setNewOverride({
        user_id: '',
        permission_key: '',
        scope_type: 'global',
        scope_id: '',
        duration_days: 30,
        reason: ''
      });
      loadOverrides();
    } catch (error) {
      alert('Failed to create override: ' + error.message);
    }
  };

  const handleRevokeOverride = async (overrideId) => {
    if (!window.confirm('Revoke this permission override?')) return;
    try {
      await api.revokePermissionOverride(overrideId);
      loadOverrides();
    } catch (error) {
      alert('Failed to revoke override: ' + error.message);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const getRoleColor = (roleKey) => {
    const colors = {
      'sysadmin': 'bg-[var(--clr-sev-blocked)]',
      'pmo_power': 'bg-purple-500',
      'program_admin': 'bg-[var(--clr-brand-primary)] bg-opacity-100',
      'pm': 'bg-[var(--clr-sev-healthy)]',
      'viewer': 'bg-[var(--clr-surface-subtle)]0'
    };
    return colors[roleKey] || 'bg-[var(--clr-surface-subtle)]0';
  };

  const formatPermissionName = (perm) => {
    return perm.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const hasPermission = (roleKey, permission) => {
    if (editMode && roleKey !== 'sysadmin') {
      return (editedPermissions[roleKey] || []).includes(permission);
    }
    if (!matrixData || !matrixData.matrix) return false;
    for (const category of Object.values(matrixData.matrix)) {
      if (category[permission] && category[permission][roleKey] !== undefined) {
        return category[permission][roleKey];
      }
    }
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--clr-brand-primary)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Shield className="w-7 h-7 text-purple-600" />
            Roles & Permissions
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Configure role permissions, access control matrix, and temporary overrides</p>
        </div>
        {activeTab === 'roles' && (
          <button
            onClick={() => setEditMode(!editMode)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              editMode 
                ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
            data-testid="toggle-edit-mode"
          >
            <Edit2 className="w-4 h-4" />
            {editMode ? 'Exit Edit Mode' : 'Configure Permissions'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab('roles')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'roles'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'
            }`}
            data-testid="tab-roles"
          >
            Roles & Matrix
          </button>
          <button
            onClick={() => setActiveTab('overrides')}
            className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors flex items-center gap-1 ${
              activeTab === 'overrides'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-[var(--clr-text-muted)] hover:text-gray-700'
            }`}
            data-testid="tab-overrides"
          >
            Permission Overrides
            {overrides.length > 0 && (
              <span className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">
                {overrides.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {activeTab === 'roles' ? (
        <>
          {editMode && (
            <div className="bg-[var(--clr-sev-atrisk)] bg-opacity-10 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Edit2 className="w-5 h-5 text-[var(--clr-sev-atrisk)] mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-900">Edit Mode Active</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Click on permission checkboxes to toggle them for each role. System Admin permissions cannot be modified.
                    Save changes for each role individually using the Save button.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Role Cards with Save/Reset buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {matrixData && matrixData.roles && matrixData.roles.map((role) => (
              <div 
                key={role.key}
                className={`bg-[var(--clr-surface-card)] rounded-lg border p-4 shadow-[var(--shadow-card)] ${!role.editable ? 'ring-2 ring-red-200' : ''}`}
                data-testid={`role-card-${role.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-3 h-3 rounded-full ${getRoleColor(role.key)}`}></div>
                  <h3 className="font-semibold text-[var(--clr-text-primary)] text-sm">{role.name}</h3>
                  {!role.editable && <Lock className="w-3 h-3 text-[var(--clr-sev-blocked)]" />}
                </div>
                <p className="text-xs text-[var(--clr-text-secondary)] mb-3">
                  {(editedPermissions[role.key] || []).length} permissions
                </p>
                {editMode && role.editable && (
                  <div className="flex gap-2 pt-2 border-t">
                    <button
                      onClick={() => handleSaveRolePermissions(role.key)}
                      disabled={savingRole === role.key}
                      className="flex-1 px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid={`save-role-${role.key}`}
                    >
                      <Save className="w-3 h-3" />
                      Save
                    </button>
                    <button
                      onClick={() => handleResetRolePermissions(role.key)}
                      disabled={savingRole === role.key}
                      className="flex-1 px-2 py-1 bg-[var(--clr-surface-subtle)] text-gray-700 text-xs rounded hover:bg-gray-200 disabled:opacity-50 flex items-center justify-center gap-1"
                      data-testid={`reset-role-${role.key}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                      Reset
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Permissions Matrix */}
          <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden">
            <div className="p-4 border-b bg-[var(--clr-surface-subtle)] flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Permissions Matrix</h2>
                <p className="text-sm text-[var(--clr-text-secondary)]">
                  {editMode ? 'Click checkboxes to configure permissions per role' : 'View and configure capabilities by role'}
                </p>
              </div>
              {matrixData && matrixData.has_custom_overrides && (
                <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                  Custom overrides active
                </span>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[var(--clr-surface-subtle)] border-b sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase min-w-[200px]">
                      Permission
                    </th>
                    {matrixData && matrixData.roles && matrixData.roles.map((role) => (
                      <th key={role.key} className="text-center px-3 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase min-w-[100px]">
                        <div className="flex flex-col items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${getRoleColor(role.key)}`}></div>
                          <span className="whitespace-nowrap">{role.name}</span>
                          {!role.editable && <Lock className="w-3 h-3 text-red-400" />}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData && matrixData.matrix && Object.entries(matrixData.matrix).map(([category, permissions]) => (
                    <React.Fragment key={category}>
                      <tr 
                        className="bg-[var(--clr-surface-subtle)] cursor-pointer hover:bg-gray-200"
                        onClick={() => toggleCategory(category)}
                      >
                        <td colSpan={matrixData.roles.length + 1} className="px-4 py-2">
                          <div className="flex items-center gap-2">
                            {expandedCategories.has(category) 
                              ? <ChevronDown className="w-4 h-4 text-[var(--clr-text-muted)]" />
                              : <ChevronRight className="w-4 h-4 text-[var(--clr-text-muted)]" />
                            }
                            <span className="font-medium text-gray-700">{category}</span>
                            <span className="text-xs text-[var(--clr-text-muted)]">({Object.keys(permissions).length} permissions)</span>
                          </div>
                        </td>
                      </tr>
                      {expandedCategories.has(category) && Object.entries(permissions).map(([permission, rolePerms]) => (
                        <tr key={permission} className="border-b hover:bg-[var(--clr-surface-hover)]" data-testid={`perm-row-${permission}`}>
                          <td className="px-4 py-2 pl-10">
                            <span className="text-sm text-gray-700">{formatPermissionName(permission)}</span>
                          </td>
                          {matrixData.roles.map((role) => {
                            const hasPerm = hasPermission(role.key, permission);
                            const canEdit = editMode && role.editable;
                            return (
                              <td key={role.key} className="text-center px-3 py-2">
                                {canEdit ? (
                                  <button
                                    onClick={() => handleTogglePermission(role.key, permission)}
                                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                                      hasPerm 
                                        ? 'bg-[var(--clr-sev-healthy)] border-green-500 text-white' 
                                        : 'bg-white border-gray-300 hover:border-green-400'
                                    }`}
                                    data-testid={`toggle-${role.key}-${permission}`}
                                  >
                                    {hasPerm && <Check className="w-4 h-4" />}
                                  </button>
                                ) : (
                                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded ${
                                    hasPerm ? 'text-[var(--clr-sev-healthy)]' : 'text-gray-300'
                                  }`}>
                                    {hasPerm ? <Check className="w-5 h-5" /> : <X className="w-4 h-4" />}
                                  </span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        /* Overrides Tab */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="bg-[var(--clr-brand-primary)] rounded-lg p-4 flex-1 mr-4">
              <h3 className="font-medium text-white">Temporary Permission Overrides</h3>
              <p className="text-sm text-blue-100 mt-1">
                Grant temporary permissions to specific users without changing their role. Overrides expire automatically.
              </p>
            </div>
            <button
              onClick={() => setShowCreateOverride(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              data-testid="create-override-btn"
            >
              <Plus className="w-4 h-4" />
              Grant Override
            </button>
          </div>

          <div className="bg-[var(--clr-surface-card)] rounded-lg border shadow-[var(--shadow-card)] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[var(--clr-surface-subtle)] border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">User</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Permission</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Scope</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Expires</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Reason</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-[var(--clr-text-secondary)] uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {overrides.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[var(--clr-text-muted)]">
                      <Clock className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      <p>No active permission overrides</p>
                    </td>
                  </tr>
                ) : (
                  overrides.map((override) => (
                    <tr key={override.id} className="hover:bg-[var(--clr-surface-hover)]" data-testid={`override-${override.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[var(--clr-text-muted)]" />
                          <span className="text-sm font-medium text-[var(--clr-text-primary)]">{override.user_name || override.user_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded">
                          {formatPermissionName(override.permission_key)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)]">
                        {override.scope_type} {override.scope_id && `(${override.scope_id.slice(-6)})`}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="w-4 h-4 text-[var(--clr-text-muted)]" />
                          {override.expires_at ? new Date(override.expires_at).toLocaleDateString() : 'Never'}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-[var(--clr-text-secondary)] max-w-xs truncate">
                        {override.reason}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => handleRevokeOverride(override.id)}
                          className="p-1 text-[var(--clr-sev-blocked)] hover:bg-[var(--clr-sev-blocked)] bg-opacity-10 rounded"
                          data-testid={`revoke-${override.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Override Modal */}
      {showCreateOverride && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowCreateOverride(false)}></div>
          <div className="relative bg-[var(--clr-surface-card)] rounded-xl shadow-xl w-full max-w-md p-6 z-10">
            <h2 className="text-xl font-bold text-[var(--clr-text-primary)] mb-4">Grant Permission Override</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User *</label>
                <select
                  value={newOverride.user_id}
                  onChange={(e) => setNewOverride(prev => ({ ...prev, user_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="override-user-select"
                >
                  <option value="">Select user...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission *</label>
                <select
                  value={newOverride.permission_key}
                  onChange={(e) => setNewOverride(prev => ({ ...prev, permission_key: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="override-permission-select"
                >
                  <option value="">Select permission...</option>
                  {allPermissions.map(p => (
                    <option key={p} value={p}>{formatPermissionName(p)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                <input
                  type="number"
                  value={newOverride.duration_days}
                  onChange={(e) => setNewOverride(prev => ({ ...prev, duration_days: parseInt(e.target.value) || 30 }))}
                  className="w-full px-3 py-2 border rounded-lg"
                  min="1"
                  max="365"
                  data-testid="override-duration-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  value={newOverride.reason}
                  onChange={(e) => setNewOverride(prev => ({ ...prev, reason: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg h-20"
                  placeholder="Why is this override needed?"
                  data-testid="override-reason-input"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateOverride(false)}
                className="px-4 py-2 border rounded-lg hover:bg-[var(--clr-surface-hover)]"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOverride}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                data-testid="submit-override-btn"
              >
                Grant Override
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPermissions;
